/**
 * Cost model (BENCH-V2-SPEC §9.5, §10). Pure: the customer confirms the numbers before anything is sent. Prices are USD per
 * million tokens, Anthropic list prices as recorded in the session protocol, for the allowlisted model only. Two estimators:
 * `estimateCost` (the v1 loop, chunk by chunk) and `estimateRunCost` (the v2 loop, from the planned units and the constants of
 * `cost-constants.json`). `costOfUsage` prices what the provider reported, cache tokens included.
 */
import {
	type AllowedModel,
	CACHE_MIN_TOKENS,
	DEFAULT_MODEL,
	type Effort,
	JUDGE_EFFORT,
	LATEST_HAIKU,
	LATEST_SONNET,
} from '../../shared/models.ts';
import { BRIEF_MAX_TOKENS, BRIEF_WINDOW_WORDS, briefWindowCount } from './brief.ts';
import constants from './cost-constants.json' with { type: 'json' };
import { GUARDRAILS_MAX_VERSIONS } from './guardrails-flags.ts';
import { estimateTokens, maxTokensFor } from './prompts.ts';
import { MAX_VERSIONS } from './prompts2.ts';
import type { CompletionUsage } from './types.ts';

/** The Economy (Message Batches) discount on every rate: the one constant (spec §9.5, §10). */
export const ECONOMY_DISCOUNT = 0.5;

export interface ModelPrice {
	input: number;
	output: number;
	/** Cache write with the 5-minute TTL (×1.25 of input). */
	cacheWrite5m: number;
	/** Cache write with the 1-hour TTL (×2 of input). */
	cacheWrite1h: number;
	/** Cache read (×0.1 of input). */
	cacheRead: number;
}

/** Only allowlisted models have a price: an unknown model is a bug, not a default. */
export const PRICING: Readonly<Record<AllowedModel, ModelPrice>> = {
	[LATEST_SONNET]: { input: 2, output: 10, cacheWrite5m: 2.5, cacheWrite1h: 4, cacheRead: 0.2 },
	// Haiku 5.5 list price for prompts up to 100k tokens (a bench request is far shorter); the cache rates follow the
	// same multipliers as Sonnet's (×1.25, ×2, ×0.1 of input).
	[LATEST_HAIKU]: {
		input: 0.1,
		output: 0.5,
		cacheWrite5m: 0.125,
		cacheWrite1h: 0.2,
		cacheRead: 0.01,
	},
};

export function priceOf(model: string): ModelPrice {
	return PRICING[model as AllowedModel] ?? PRICING[DEFAULT_MODEL];
}

export type CostConstants = typeof constants;
export const COST_CONSTANTS: CostConstants = constants;

export interface CostOptions {
	model?: string;
	maxAttempts?: number;
	/** Expected number of attempts per chunk (default 1.75: the first always, a second half the time, a third a quarter). */
	expectedAttempts?: number;
	/** Tokens of the system prompt (preamble, directives, rules). Default 450. */
	systemTokens?: number;
	/** Economy: every rate is discounted by `ECONOMY_DISCOUNT`. */
	economy?: boolean;
}

export interface CostEstimate {
	model: string;
	chunks: number;
	attempts: { expected: number; max: number };
	inputTokens: { expected: number; max: number };
	outputTokens: { expected: number; max: number };
	expectedUsd: number;
	maxUsd: number;
}

const usd = (input: number, output: number, p: ModelPrice): number =>
	(input * p.input + output * p.output) / 1_000_000;

const discount = (economy: boolean | undefined): number => (economy ? 1 - ECONOMY_DISCOUNT : 1);

/**
 * Expected and maximum cost of rewriting the chunks. An attempt costs the system prompt plus the chunk; from the second attempt
 * the user message also carries the previous output and the feedback (about one more chunk and 150 tokens). The expected output
 * is the chunk's own length; the maximum is the request's `max_tokens`.
 */
export function estimateCost(chunkTexts: readonly string[], opts: CostOptions = {}): CostEstimate {
	const model = opts.model ?? DEFAULT_MODEL;
	const price = priceOf(model);
	const maxAttempts = opts.maxAttempts ?? 3;
	const expectedAttempts = Math.min(opts.expectedAttempts ?? 1.75, maxAttempts);
	const sys = opts.systemTokens ?? 450;
	let inExp = 0;
	let outExp = 0;
	let inMax = 0;
	let outMax = 0;
	for (const text of chunkTexts) {
		const t = estimateTokens(text);
		const mt = maxTokensFor(text);
		const first = sys + t + 20;
		const later = sys + 2 * t + 170;
		inExp += first + (expectedAttempts - 1) * later;
		outExp += expectedAttempts * t;
		inMax += first + (maxAttempts - 1) * later;
		outMax += maxAttempts * mt;
	}
	return {
		model,
		chunks: chunkTexts.length,
		attempts: { expected: expectedAttempts, max: maxAttempts },
		inputTokens: { expected: Math.round(inExp), max: Math.round(inMax) },
		outputTokens: { expected: Math.round(outExp), max: Math.round(outMax) },
		expectedUsd: usd(inExp, outExp, price) * discount(opts.economy),
		maxUsd: usd(inMax, outMax, price) * discount(opts.economy),
	};
}

export interface UsageCostOptions {
	/** The usage came from a batch: every rate is discounted by `ECONOMY_DISCOUNT`. */
	economy?: boolean;
	/** TTL of the cache writes (default `5m`, what `cache_control: {type: 'ephemeral'}` gives). */
	cacheTtl?: '5m' | '1h';
}

/**
 * Actual cost of the usage the provider reported: input, output, cache writes (5 minutes ×1.25, 1 hour ×2) and cache reads
 * (×0.1). Anthropic reports the cache tokens separately from `input_tokens`, so the four add up. Thinking is in the output.
 */
export function costOfUsage(
	model: string,
	usage: CompletionUsage | undefined,
	opts: UsageCostOptions = {}
): number {
	if (!usage) return 0;
	const p = priceOf(model);
	const write = opts.cacheTtl === '1h' ? p.cacheWrite1h : p.cacheWrite5m;
	const total =
		usd(usage.inputTokens, usage.outputTokens, p) +
		((usage.cacheWriteTokens ?? 0) * write + (usage.cacheReadTokens ?? 0) * p.cacheRead) /
			1_000_000;
	return total * discount(opts.economy);
}

/* ---------------------------------------------------------------- v2 estimate (spec §10) */

export interface RunChunkShape {
	/** Words of the chunk the request carries. */
	words: number;
	/** Planned units in the chunk, and the words they hold (the model writes these back). */
	units: number;
	unitWords: number;
	/** The units' routes decide the expected attempts; default `D` (the highest). */
	route?: 'S' | 'P' | 'D';
}

/** Past this many words a document's default is two versions per passage at most (BENCH-V3-PLAN §6.3, D4). */
export const VERSIONS_FULL_MAX_WORDS = 100_000;

/**
 * The default versions per passage (BENCH-V3-PLAN §6.3, DESIGN ED-90, D4): three on Haiku, where the visible text is a sixth
 * of a call's output; two on Sonnet and on any document above 100,000 words. A setting in the cost dialog.
 */
export function defaultVersions(model: string, words: number): number {
	const byModel = model === LATEST_HAIKU ? 3 : 2;
	return words > VERSIONS_FULL_MAX_WORDS ? Math.min(2, byModel) : byModel;
}

export interface RunCostOptions {
	model?: string;
	effort?: Effort;
	/** Versions per passage, 1 to 3 (default 1): the visible text of a call is written that many times. */
	versions?: number;
	economy?: boolean;
	/** Overrides of `cost-constants.json` (the pilot's values, tests). */
	constants?: Partial<CostConstants>;
	/** Words of an average paragraph, for the controls (default 120). */
	controlWords?: number;
	/**
	 * Words of a document whose brief is still to be made (BENCH-V3-PLAN §8): its calls, one per window of 30,000 words at the
	 * latest Sonnet and `medium` effort, are a line of their own (`parts.brief`). 0 or absent: no brief calls (stored brief).
	 */
	briefWords?: number;
	/**
	 * Tokens the brief adds to the system prompt (`estimateBriefTokens`, or the stored brief's). The system prompt then also
	 * reaches the cache minimum, so it is written once and read after. The controls carry no notes and stay uncached.
	 */
	briefTokens?: number;
	/**
	 * The editorial guardrails (ED-191): the system prompt carries their block (`guardrails.systemTokens`, equal to `GUARDRAILS_SYSTEM_TOKENS`; controls included),
	 * each rewrite request grows by `guardrails.directiveDeltaTokensPerRequest` (the directive block, and since GR2 the input growth
	 * the G2 pilot measured), every passage's answer carries a why
	 * (`guardrails.whyTokensPerVersion` per version, GR2), and versions are capped at the strategies the
	 * guardrails allow (two: `recast` is left out).
	 */
	guardrails?: boolean;
	/** With the guardrails, the meaning review (GR3, ED-196, dormant by default): its block line and one judge call per answer. */
	review?: boolean;
}

export interface RunCostEstimate {
	model: string;
	effort: Effort;
	chunks: number;
	requests: { expected: number; max: number };
	expectedUsd: number;
	maxUsd: number;
	/** Calls of the document brief still to be made (0 when its brief is stored or the document is empty). */
	briefCalls: number;
	/** The parts of the expected figure. */
	/** `review`: the meaning review's judge calls (GR3), present only when it is on. */
	parts: { rewrite: number; judge: number; controls: number; brief: number; review?: number };
	constantsDate: string;
}

/**
 * Expected and maximum cost of a v2 run (v3 layout, ED-88/ED-96). Per request: input = system + directive block + chunk +
 * notes; output = the unit words + JSON overhead, plus thinking = θ(effort) × (input + output).
 * - The system prompt is short (preamble, route block, always-on rules, output contract) and cached with a one-hour TTL: it
 *   is written once per distinct system prompt (the distinct routes among the chunks, at least one, stand for the route
 *   classes; the first requests of the run pay `cacheWrite1h`) and read on every later request. It is cached only when it
 *   reaches the model's `CACHE_MIN_TOKENS`; below that the API silently processes it uncached, and the estimate prices it as
 *   plain input on every request.
 * - The directive block (`directiveTokensPerRequest`) is part of each request's uncached user input, retries included. A
 *   control request carries none.
 * - Judge calls run at θ(`JUDGE_EFFORT`), not the run's effort.
 * Expected: the planned units × the expected attempts of their route. Maximum: every chunk at `maxAttempts`, the judge on
 * every chunk, the controls, thinking at `thinkingHeadroomFactor` × the expected thinking, and the system prompt never cached
 * (directive tokens included).
 */
export function estimateRunCost(
	chunks: readonly RunChunkShape[],
	opts: RunCostOptions = {}
): RunCostEstimate {
	const k = { ...COST_CONSTANTS, ...opts.constants };
	const model = opts.model ?? DEFAULT_MODEL;
	const effort = opts.effort ?? 'high';
	const maxVersions = opts.guardrails ? GUARDRAILS_MAX_VERSIONS : MAX_VERSIONS;
	const kv = Math.max(1, Math.min(maxVersions, Math.floor(opts.versions ?? 1)));
	const p = priceOf(model);
	const theta = k.theta[effort];
	const tpw = k.tokensPerWord;
	// the meaning review (GR3) only with the guardrails: its line of the block and its judge calls
	const rvOn = opts.guardrails === true && opts.review === true;
	const gr = opts.guardrails
		? k.guardrails.systemTokens + (rvOn ? k.guardrails.review.systemLineTokens : 0)
		: 0;
	const sysCtrl = k.systemTokens + gr;
	const sys = k.systemTokens + gr + (opts.briefTokens ?? 0);
	const dir =
		k.directiveTokensPerRequest +
		(opts.guardrails ? k.guardrails.directiveDeltaTokensPerRequest : 0);
	const thetaJudge = k.theta[JUDGE_EFFORT];
	const cacheMin = CACHE_MIN_TOKENS[model as AllowedModel] ?? CACHE_MIN_TOKENS[DEFAULT_MODEL];
	const cacheable = sys >= cacheMin;
	const withNotes = (opts.briefTokens ?? 0) > 0;
	const d = discount(opts.economy);
	// one cache write per distinct system prompt: the route classes among the chunks (S, P and D stand for sentence,
	// paragraph and mixed), at least one
	const writesTotal = Math.max(
		1,
		new Set(chunks.filter((c) => c.units > 0).map((c) => c.route ?? 'D')).size
	);
	let writesLeft = writesTotal;
	// Dollars of the system part of `weight` expected requests: written (1 hour) while writes remain, else read; uncached
	// input when the prompt is below the cache minimum.
	const systemPart = (weight: number): number => {
		if (!cacheable) return (weight * sys * p.input) / 1_000_000;
		const w = Math.min(weight, writesLeft);
		writesLeft -= w;
		return (sys * (w * p.cacheWrite1h + (weight - w) * p.cacheRead)) / 1_000_000;
	};

	let requestsExp = 0;
	let requestsMax = 0;
	let rewriteExp = 0;
	let rewriteMax = 0;
	// Dollars of one request, in million-token units already applied.
	const price = (uncachedIn: number, out: number, think: number): number =>
		(uncachedIn * p.input + (out + think) * p.output) / 1_000_000;

	for (const c of chunks) {
		if (c.units === 0) continue;
		const chunkTok = c.words * tpw;
		// the visible text is written k times; the thinking is priced on one version's text and grows by
		// `thinkingGrowthPerVersion` for each further version (measured: Sonnet 5.5 high, ch62, +63% for two versions)
		const outTok1 = c.unitWords * tpw + c.units * k.jsonOverheadTokensPerUnit;
		// under the guardrails every passage (every version of it) carries an action and a one-sentence why (GR2, ED-194)
		const whyTok = opts.guardrails ? c.units * kv * k.guardrails.whyTokensPerVersion : 0;
		const outTok =
			(kv === 1
				? outTok1
				: c.unitWords * tpw * kv +
					c.units * (k.jsonOverheadTokensPerUnit + kv * k.versions.jsonTokensPerVersion)) + whyTok;
		const versionsBlock = kv === 1 ? 0 : k.versions.blockTokens + kv * k.versions.lineTokens;
		const notes = c.units * k.notesTokensPerUnit;
		const attemptsExp = Math.min(k.expectedAttempts[c.route ?? 'D'], k.maxAttempts);
		for (const [attempts, isMax] of [
			[attemptsExp, false],
			[k.maxAttempts, true],
		] as const) {
			let cost = 0;
			for (let a = 1; a <= Math.ceil(attempts); a++) {
				const weight = Math.min(1, attempts - (a - 1));
				// The directive block rides in every request; from the second attempt the message also carries the previous
				// version (the unit words again).
				const user = dir + versionsBlock + chunkTok + notes + (a > 1 ? c.unitWords * tpw : 0);
				const think =
					theta *
					(sys + user + outTok1) *
					(1 + (kv - 1) * k.versions.thinkingGrowthPerVersion) *
					(isMax ? k.thinkingHeadroomFactor : 1);
				cost += isMax
					? weight * price(sys + user, outTok, think)
					: weight * price(user, outTok, think) + systemPart(weight);
			}
			if (isMax) {
				rewriteMax += cost;
				requestsMax += attempts;
			} else {
				rewriteExp += cost;
				requestsExp += attempts;
			}
		}
	}

	const live = chunks.filter((c) => c.units > 0);
	// The judge's own system text is far below the cache minimum: plain input, whatever the run's effort (ED-96).
	const judgeUser = k.judgeLinesPerChunk * k.judgeTokensPerLine;
	const judgeOut = k.judgeLinesPerChunk * 25;
	// the judge's system text does not carry the guardrails block
	const sysJudge = (sys - gr) / 2;
	const judgeThink = thetaJudge * (sysJudge + judgeUser + judgeOut);
	const judgeReq = (): number => price(sysJudge + judgeUser, judgeOut, judgeThink);
	const judgeMaxReq = (): number =>
		price(sysJudge + judgeUser, judgeOut, k.thinkingHeadroomFactor * judgeThink);
	const judgeExp = live.length * k.judgeShare * judgeReq();
	const judgeMax = live.length * judgeMaxReq();
	// the guardrails' review (GR3, ED-196): one judge call per answer, at the judge's effort, priced as plain input; its items are every
	// version of every passage, each with the original and its rewrite
	let reviewExp = 0;
	let reviewMax = 0;
	let reviewCalls = 0;
	let reviewCallsMax = 0;
	if (rvOn) {
		const rv = k.guardrails.review;
		for (const c of live) {
			const items = c.units * kv;
			const inTok = rv.systemTokens + c.unitWords * tpw * (1 + kv) + items * rv.itemOverheadTokens;
			const out = items * rv.outputTokensPerItem;
			const think = thetaJudge * (inTok + out);
			const calls =
				rv.answersReviewed * Math.min(k.expectedAttempts[c.route ?? 'D'], k.maxAttempts);
			reviewExp += calls * price(inTok, out, think);
			reviewMax += k.maxAttempts * price(inTok, out, k.thinkingHeadroomFactor * think);
			reviewCalls += calls;
			reviewCallsMax += k.maxAttempts;
		}
	}
	const nCtrl =
		live.length === 0 ? 0 : Math.min(k.controls.max, Math.ceil(k.controls.share * live.length));
	const ctrlWords = opts.controlWords ?? 120;
	const ctrlUser = ctrlWords * tpw + k.notesTokensPerUnit;
	const ctrlOut = ctrlWords * tpw + k.jsonOverheadTokensPerUnit;
	const ctrlThink = theta * (sysCtrl + ctrlUser + ctrlOut);
	// controls carry no document notes: with a brief their system prompt is the short one, below the cache minimum (plain input),
	// unless the guardrails block lifts it over: then it is a prompt of its own, written once and read after
	const ctrlSys = withNotes
		? sysCtrl >= cacheMin && nCtrl > 0
			? (sysCtrl * (p.cacheWrite1h + (nCtrl - 1) * p.cacheRead)) / 1_000_000
			: (nCtrl * sysCtrl * p.input) / 1_000_000
		: nCtrl > 0
			? systemPart(nCtrl)
			: 0;
	const ctrlExp = nCtrl * price(ctrlUser, ctrlOut, ctrlThink) + ctrlSys;
	const ctrlMax = nCtrl * price(sysCtrl + ctrlUser, ctrlOut, k.thinkingHeadroomFactor * ctrlThink);

	// the brief (BENCH-V3-PLAN §8): real-time calls at the latest Sonnet and `medium`, whatever the run's model and mode
	const sonnet = priceOf(LATEST_SONNET);
	const thetaBrief = k.theta.medium;
	const briefWindows = briefWindowCount(opts.briefWords ?? 0);
	let briefExp = 0;
	let briefMax = 0;
	for (let w = 0; w < briefWindows; w++) {
		const words = Math.min(BRIEF_WINDOW_WORDS, (opts.briefWords ?? 0) - w * BRIEF_WINDOW_WORDS);
		const inTok = k.briefSystemTokens + words * tpw;
		const think = thetaBrief * (inTok + k.briefOutputTokens);
		briefExp += (inTok * sonnet.input + (k.briefOutputTokens + think) * sonnet.output) / 1_000_000;
		briefMax +=
			(inTok * sonnet.input +
				(BRIEF_MAX_TOKENS + k.thinkingHeadroomFactor * think) * sonnet.output) /
			1_000_000;
	}

	return {
		model,
		effort,
		chunks: live.length,
		requests: {
			expected: requestsExp + live.length * k.judgeShare + reviewCalls + nCtrl + briefWindows,
			max: requestsMax + live.length + reviewCallsMax + nCtrl + briefWindows,
		},
		expectedUsd: (rewriteExp + judgeExp + reviewExp + ctrlExp) * d + briefExp,
		maxUsd: (rewriteMax + judgeMax + reviewMax + ctrlMax) * d + briefMax,
		briefCalls: briefWindows,
		parts: {
			rewrite: rewriteExp * d,
			judge: judgeExp * d,
			controls: ctrlExp * d,
			brief: briefExp,
			...(rvOn ? { review: reviewExp * d } : {}),
		},
		constantsDate: k.date,
	};
}
